import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaSave, FaCamera, FaTrash, FaCropAlt } from 'react-icons/fa';
import './EditProfileModal.css';
import ImageCropperModal from '../ImageCropper/ImageCropperModal';
import API_BASE_URL from '../../config/api';

const API_BASE = API_BASE_URL;
const DEFAULT_AVATAR = 'https://images.unsplash.com/vector-1742875355318-00d715aec3e8?q=80&w=880&auto=format&fit=crop';

const EditProfileModal = ({ isOpen, onClose, userData, onSave }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        nif: '',
        address: '',
        postalCode: '',
        city: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Photo state
    const [photoPreview, setPhotoPreview] = useState(null);
    const [photoFile, setPhotoFile] = useState(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [photoError, setPhotoError] = useState('');
    const [rawImageSrc, setRawImageSrc] = useState(null);
    const [rawFileName, setRawFileName] = useState('avatar.jpg');
    const [isCropperOpen, setIsCropperOpen] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (userData && isOpen) {
            setFormData({
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                phone: userData.phone || '',
                nif: userData.nif || '',
                address: userData.address || '',
                postalCode: userData.postalCode || '',
                city: userData.city || ''
            });
            setError('');
            setSuccess('');
            setPhotoFile(null);
            setPhotoPreview(null);
            setRawImageSrc(null);
            setIsCropperOpen(false);
            setPhotoError('');
        }
    }, [userData, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (error) setError('');
    };

    const handlePhotoSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setPhotoError('Tipo de ficheiro inválido. Use JPEG, PNG ou WebP.');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setPhotoError('A imagem não pode exceder 10MB.');
            return;
        }

        setPhotoError('');
        setRawFileName(file.name || 'avatar.jpg');

        const reader = new FileReader();
        reader.onload = (ev) => {
            setRawImageSrc(ev.target.result);
            setIsCropperOpen(true);
        };
        reader.readAsDataURL(file);

        // Reset input value so the same file can be selected again
        e.target.value = '';
    };

    const handleCropComplete = (croppedFile, croppedPreviewUrl) => {
        setPhotoFile(croppedFile);
        setPhotoPreview(croppedPreviewUrl);
        setIsCropperOpen(false);
        setPhotoError('');
    };

    const handleCropCancel = () => {
        setIsCropperOpen(false);
    };

    const handleUploadPhoto = async () => {
        if (!photoFile) return;
        setUploadingPhoto(true);
        setPhotoError('');

        try {
            const token = localStorage.getItem('token');
            const userId = userData.id || localStorage.getItem('userId');

            const form = new FormData();
            form.append('photo', photoFile);

            const res = await fetch(`${API_BASE}/api/users/${userId}/profile-photo`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: form
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.message || 'Erro ao carregar foto');
            }

            const data = await res.json();
            const updatedUser = { ...userData, profilePhotoUrl: data.profilePhotoUrl };
            onSave(updatedUser);
            setPhotoFile(null);
            setSuccess('Foto atualizada com sucesso!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setPhotoError(err.message);
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handleRemovePhoto = async () => {
        setUploadingPhoto(true);
        setPhotoError('');

        try {
            const token = localStorage.getItem('token');
            const userId = userData.id || localStorage.getItem('userId');

            const res = await fetch(`${API_BASE}/api/users/${userId}/profile-photo`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok && res.status !== 204) {
                throw new Error('Erro ao remover foto');
            }

            const updatedUser = { ...userData, profilePhotoUrl: null };
            onSave(updatedUser);
            setPhotoPreview(null);
            setPhotoFile(null);
            setRawImageSrc(null);
            setSuccess('Foto removida.');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setPhotoError(err.message);
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const userId = userData.id || localStorage.getItem('userId');

            const payload = {
                email: userData.email,
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone,
                birthDate: userData.birthDate,
                nif: formData.nif,
                address: formData.address,
                postalCode: formData.postalCode,
                city: formData.city
            };

            const response = await fetch(`${API_BASE}/api/users/${userId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Erro ao atualizar perfil');
            }

            // Also upload photo if a new one was selected
            if (photoFile) {
                await handleUploadPhoto();
                onClose();
            } else {
                const updatedUser = { ...userData, ...formData };
                onSave(updatedUser);
                onClose();
            }
        } catch (err) {
            console.error('Error updating profile:', err);
            setError(err.message || 'Erro ao atualizar perfil. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    if (!isOpen) return null;

    const currentPhotoUrl = photoPreview
        || (userData?.profilePhotoUrl ? `${API_BASE}${userData.profilePhotoUrl}` : null)
        || DEFAULT_AVATAR;

    const hasCustomPhoto = !!(photoPreview || userData?.profilePhotoUrl);

    return (
        <div className="modal-overlay" onMouseDown={handleOverlayClick}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2><i className="fas fa-user-edit"></i> Editar Perfil</h2>
                    <button className="close-btn" onClick={onClose} disabled={loading || uploadingPhoto}>
                        <FaTimes />
                    </button>
                </div>

                {/* Photo Section */}
                <div className="photo-upload-section">
                    <div className="photo-upload-wrapper">
                        <img
                            src={currentPhotoUrl}
                            alt="Foto de perfil"
                            className="photo-preview-img"
                            onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                        />
                        <button
                            type="button"
                            className="photo-change-btn"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingPhoto}
                            title="Escolher nova foto"
                        >
                            <FaCamera />
                        </button>
                        {rawImageSrc && (
                            <button
                                type="button"
                                className="photo-crop-badge-btn"
                                onClick={() => setIsCropperOpen(true)}
                                disabled={uploadingPhoto}
                                title="Recortar / Ajustar enquadramento"
                            >
                                <FaCropAlt />
                            </button>
                        )}
                    </div>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handlePhotoSelect}
                        style={{ display: 'none' }}
                    />

                    <div className="photo-actions">
                        {photoFile && (
                            <>
                                <button
                                    type="button"
                                    className="btn-upload-photo"
                                    onClick={handleUploadPhoto}
                                    disabled={uploadingPhoto}
                                >
                                    {uploadingPhoto ? 'A carregar...' : 'Guardar Foto'}
                                </button>
                                <button
                                    type="button"
                                    className="btn-recrop-photo"
                                    onClick={() => setIsCropperOpen(true)}
                                    disabled={uploadingPhoto}
                                    title="Ajustar enquadramento"
                                >
                                    <FaCropAlt /> Recortar
                                </button>
                            </>
                        )}
                        {hasCustomPhoto && !photoFile && (
                            <button
                                type="button"
                                className="btn-remove-photo"
                                onClick={handleRemovePhoto}
                                disabled={uploadingPhoto}
                                title="Remover foto"
                            >
                                <FaTrash /> Remover Foto
                            </button>
                        )}
                    </div>

                    {photoError && <p className="photo-error-msg">{photoError}</p>}
                    {success && <p className="photo-success-msg">{success}</p>}
                </div>

                <form onSubmit={handleSubmit}>
                    {error && <div className="error-message">{error}</div>}

                    <div className="form-row">
                        <div className="form-group">
                            <label>Nome Próprio</label>
                            <div className="input-wrapper">
                                <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    disabled={loading}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Apelido</label>
                            <div className="input-wrapper">
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    disabled={loading}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Telemóvel</label>
                            <div className="input-wrapper">
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="+351 912 345 678"
                                    disabled={loading}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>NIF</label>
                            <div className="input-wrapper">
                                <input
                                    type="text"
                                    name="nif"
                                    value={formData.nif}
                                    onChange={handleChange}
                                    placeholder="123456789"
                                    maxLength="9"
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Morada</label>
                        <div className="input-wrapper">
                            <input
                                type="text"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder="Rua, número, andar"
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Código Postal</label>
                            <div className="input-wrapper">
                                <input
                                    type="text"
                                    name="postalCode"
                                    value={formData.postalCode}
                                    onChange={handleChange}
                                    placeholder="4490-500"
                                    disabled={loading}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Localidade</label>
                            <div className="input-wrapper">
                                <input
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    placeholder="Póvoa de Varzim"
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="btn-cancel"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn-save"
                            disabled={loading}
                        >
                            <FaSave />
                            {loading ? 'A guardar...' : 'Guardar Alterações'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Image Cropper Modal */}
            <ImageCropperModal
                isOpen={isCropperOpen}
                imageSrc={rawImageSrc}
                fileName={rawFileName}
                onCropComplete={handleCropComplete}
                onCancel={handleCropCancel}
            />
        </div>
    );
};

export default EditProfileModal;
